import React from "react";
import { Alert, Box, Button, Card, Stack, Text, Drawer, TextField, Table, TableHeader, TableBody, TableRow, TableHeaderCell, TableCell, Inline } from "braid-design-system";
import { useCustomerAccounts } from "./accounts/useCustomerAccounts";
import { useVendorAccounts } from "./accounts/useVendorAccounts";
import { getAccountEntries } from "../services/accountEntryService";
import { getAllPaymentModes, PaymentModeResponse } from "../services/paymentModeService";
import { CustomerReceipt, saveAccountEntriesPayment } from "../services/accountPaymentService";
import { getSystemSetup, SetupResponse } from "../services/setupService";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthProvider";
import { useLocation } from "react-router-dom";
import { sanitizeNumericString } from "../utils/numericInput";
import CustomerReceiptHistoryViewer, { formatReceiptDate } from "./accounts/CustomerReceiptHistoryViewer";
import { printCustomerReceipt, SavedReceiptSnapshot } from "./accounts/customerReceiptPrint";
import "../styles/Accounts.css";

type AccountRow = {
    id: string;
    transactionId: number | string | null;
    traNo: string;
    traType: string;
    date: string;
    accountNo: string;
    traAmount: string;
    amount: string;
};

type AccountsMode = "supplier-payments" | "customer-receipts";
type PartyType = "vendor" | "customer";
type PaymentDetailKind = "cheque" | "card" | "other" | "none";

type RowErrors = Record<string, string>;

const getAccountsMode = (search: string): AccountsMode => {
    const normalizedSearch = search.toLowerCase();
    if (normalizedSearch.includes("customerreceipts")) {
        return "customer-receipts";
    }

    return "supplier-payments";
};

const getPartyType = (mode: AccountsMode): PartyType => (mode === "customer-receipts" ? "customer" : "vendor");

const normalizeLookupValue = (value: string): string => value.trim().toLowerCase();

const toDisplayValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const formatDateValue = (value: unknown): string => {
    if (!value) {
        return "";
    }

    const rawValue = String(value);
    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return rawValue;
    }

    return new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(parsedDate);
};

const parseAmountValue = (value: string): number | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatAmountValue = (value: number): string => value.toFixed(2);

const getPaymentModeLabel = (mode: PaymentModeResponse | null): string =>
    mode?.paytypedesc?.trim() || mode?.paycode || "";

const getPaymentDetailKind = (mode: PaymentModeResponse | null): PaymentDetailKind => {
    const value = `${mode?.paycode ?? ""} ${mode?.paytypedesc ?? ""}`.toLowerCase();

    if (value.includes("cheque") || value.includes("check")) {
        return "cheque";
    }

    if (value.includes("card")) {
        return "card";
    }

    if (value.includes("other")) {
        return "other";
    }

    return "none";
};

const getAllocationError = (row: AccountRow): string | null => {
    const allocatedAmount = parseAmountValue(row.amount);
    const transactionAmount = parseAmountValue(row.traAmount);

    if (allocatedAmount === null || transactionAmount === null) {
        return null;
    }

    if (transactionAmount < 0) {
        return "Transaction amount must be non-negative.";
    }

    if (allocatedAmount < 0) {
        return "Allocated amount must be non-negative.";
    }

    if (allocatedAmount > transactionAmount) {
        return `Allocated amount cannot exceed ${formatAmountValue(transactionAmount)}.`;
    }

    return null;
};

const Accounts: React.FC = () => {
    const toast = useToast();
    const location = useLocation();
    const accountsMode = React.useMemo(() => getAccountsMode(location.search), [location.search]);
    const partyType = React.useMemo(() => getPartyType(accountsMode), [accountsMode]);
    const [searchText, setSearchText] = React.useState("");
    const [isCustomerDrawerOpen, setCustomerDrawerOpen] = React.useState(false);
    const [drawerSearchTerm, setDrawerSearchTerm] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [rows, setRows] = React.useState<AccountRow[]>([]);
    const [rowErrors, setRowErrors] = React.useState<RowErrors>({});
    const [rowsLoading, setRowsLoading] = React.useState(false);
    const [rowsError, setRowsError] = React.useState<string | null>(null);
    const [paymentModes, setPaymentModes] = React.useState<PaymentModeResponse[]>([]);
    const [paymentModesLoading, setPaymentModesLoading] = React.useState(false);
    const [paymentModesError, setPaymentModesError] = React.useState<string | null>(null);
    const [selectedPaymode, setSelectedPaymode] = React.useState<string>("");
    const [chequeNo, setChequeNo] = React.useState("");
    const [chequeDate, setChequeDate] = React.useState("");
    const [cardReference, setCardReference] = React.useState("");
    const [otherReference, setOtherReference] = React.useState("");
    const [payerName, setPayerName] = React.useState("");
    const [referenceNo, setReferenceNo] = React.useState("");
    const [note, setNote] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [savedReceipt, setSavedReceipt] = React.useState<SavedReceiptSnapshot | null>(null);
    const [companySetup, setCompanySetup] = React.useState<SetupResponse | null>(null);
    const [historyRefreshKey, setHistoryRefreshKey] = React.useState(0);
    const { user } = useAuth();
    const customerHook = useCustomerAccounts(searchText);
    const vendorHook = useVendorAccounts(searchText);
    const selectedHook = accountsMode === "customer-receipts" ? customerHook : vendorHook;
    const loading = customerHook.loading || vendorHook.loading;
    const error = customerHook.error || vendorHook.error;
    const selectedAccountCode = React.useMemo(() => {
        const normalizedSearch = normalizeLookupValue(searchText);
        if (!normalizedSearch) {
            return "";
        }

        if (accountsMode === "customer-receipts") {
            const exactCustomer = customerHook.customers.find((customer) => {
                const code = normalizeLookupValue(customer.cus_code ?? "");
                const name = normalizeLookupValue(customer.cus_name ?? "");
                return code === normalizedSearch || name === normalizedSearch;
            });

            return exactCustomer?.cus_code ?? "";
        }

        const exactVendor = vendorHook.vendors.find((vendor) => {
            const code = normalizeLookupValue(vendor.ven_code ?? "");
            const name = normalizeLookupValue(vendor.ven_Name ?? "");
            return code === normalizedSearch || name === normalizedSearch;
        });

        return exactVendor?.ven_code ?? "";
    }, [accountsMode, customerHook.customers, searchText, vendorHook.vendors]);

    const accountOptions = selectedHook.accountOptions;
    const panelTitle = selectedHook.panelTitle;
    const searchLabel = selectedHook.searchLabel;
    const fieldPlaceholder = selectedHook.fieldPlaceholder;
    const fieldListId = selectedHook.fieldListId;
    const selectedPaymentMode = React.useMemo(
        () => paymentModes.find((mode) => mode.paycode === selectedPaymode) ?? null,
        [paymentModes, selectedPaymode],
    );
    const selectedPaymentModeLabel = React.useMemo(() => getPaymentModeLabel(selectedPaymentMode), [selectedPaymentMode]);
    const paymentDetailKind = React.useMemo(() => getPaymentDetailKind(selectedPaymentMode), [selectedPaymentMode]);
    const allocatedAmountTotal = React.useMemo(
        () => rows.reduce((total, row) => total + (parseAmountValue(row.amount) ?? 0), 0),
        [rows],
    );
    const hasAllocationErrors = React.useMemo(
        () => rows.some((row) => Boolean(getAllocationError(row))),
        [rows],
    );

    // Clear state when switching between account pages
    React.useEffect(() => {
        setSearchText("");
        setRows([]);
        setRowsError(null);
        setRowsLoading(false);
        setCustomerDrawerOpen(false);
        setDrawerSearchTerm("");
        setChequeNo("");
        setChequeDate("");
        setCardReference("");
        setOtherReference("");
        setPayerName("");
        setReferenceNo("");
        setNote("");
        setSavedReceipt(null);
    }, [accountsMode]);

    // Selecting a new account starts a new receipt, so the previous one can no longer be printed.
    React.useEffect(() => {
        if (selectedAccountCode) {
            setSavedReceipt(null);
        }
    }, [selectedAccountCode]);

    React.useEffect(() => {
        let mounted = true;

        getSystemSetup()
            .then((setup) => {
                if (mounted) {
                    setCompanySetup(setup);
                }
            })
            .catch(() => {
                // Company details are optional on the printed receipt; ignore failures.
            });

        return () => {
            mounted = false;
        };
    }, []);

    React.useEffect(() => {
        let mounted = true;

        const loadAccountEntries = async () => {
            if (!selectedAccountCode) {
                setRows([]);
                setRowsError(null);
                setRowsLoading(false);
                return;
            }

            try {
                setRowsLoading(true);
                setRowsError(null);
                const data = await getAccountEntries(selectedAccountCode, partyType);
                if (!mounted) {
                    return;
                }

                const mappedRows =
                    data.map((entry, index) => {
                        const transactionAmount = entry.balance ?? entry.amount ?? entry.traAmount;

                        return {
                            id: `${entry.traNo ?? "entry"}-${index}`,
                            transactionId: entry.transactionId ?? null,
                            traNo: toDisplayValue(entry.traNo),
                            traType: toDisplayValue(entry.traType),
                            date: formatDateValue(entry.date),
                            accountNo: toDisplayValue(entry.accountNo),
                            traAmount: toDisplayValue(transactionAmount),
                            amount: toDisplayValue(transactionAmount),
                        };
                    });

                setRows(mappedRows);
                setRowErrors(
                    mappedRows.reduce<RowErrors>((accumulator, row) => {
                        const error = getAllocationError(row);
                        if (error) {
                            accumulator[row.id] = error;
                        }
                        return accumulator;
                    }, {}),
                );
            } catch (err: any) {
                if (!mounted) {
                    return;
                }

                setRows([]);
                setRowsError(err?.message || "Failed to load account entries");
            } finally {
                if (mounted) {
                    setRowsLoading(false);
                }
            }
        };

        loadAccountEntries();

        return () => {
            mounted = false;
        };
    }, [partyType, selectedAccountCode]);

    React.useEffect(() => {
        let mounted = true;

        const loadPaymentModes = async () => {
            try {
                setPaymentModesLoading(true);
                setPaymentModesError(null);
                const data = await getAllPaymentModes();
                if (!mounted) {
                    return;
                }

                setPaymentModes(data);
                setSelectedPaymode((currentSelection) => currentSelection || data[0]?.paycode || "");
            } catch (err: any) {
                if (!mounted) {
                    return;
                }

                setPaymentModes([]);
                setPaymentModesError(err?.message || "Failed to load payment modes");
            } finally {
                if (mounted) {
                    setPaymentModesLoading(false);
                }
            }
        };

        loadPaymentModes();

        return () => {
            mounted = false;
        };
    }, []);

    const handleAmountChange = (rowId: string, value: string) => {
        setSavedReceipt(null);
        const sanitizedValue = sanitizeNumericString(value);
        setRows((currentRows) => {
            const nextRows = currentRows.map((row) => (row.id === rowId ? { ...row, amount: sanitizedValue } : row));
            const updatedRow = nextRows.find((row) => row.id === rowId);

            setRowErrors((currentErrors) => {
                const nextErrors = { ...currentErrors };

                if (!updatedRow) {
                    delete nextErrors[rowId];
                    return nextErrors;
                }

                const allocationError = getAllocationError(updatedRow);
                if (allocationError) {
                    nextErrors[rowId] = allocationError;
                } else {
                    delete nextErrors[rowId];
                }

                return nextErrors;
            });

            return nextRows;
        });
    };

    const handleAmountFocus = (rowId: string) => {
        setRows((currentRows) =>
            currentRows.map((row) => {
                if (row.id !== rowId) {
                    return row;
                }

                // Clear the pre-filled default (placeholder) value so the user can type a fresh amount.
                if (row.amount !== "" && row.amount === row.traAmount) {
                    return { ...row, amount: "" };
                }

                return row;
            }),
        );

        setRowErrors((currentErrors) => {
            if (!currentErrors[rowId]) {
                return currentErrors;
            }

            const nextErrors = { ...currentErrors };
            delete nextErrors[rowId];
            return nextErrors;
        });
    };

    const handleSave = async () => {
        if (hasAllocationErrors) {
            toast.showError("Fix the allocated amounts before saving.");
            return;
        }

        const firstInvalidRow = rows.find((row) => getAllocationError(row));
        if (firstInvalidRow) {
            toast.showError(`Fix the allocated amount for transaction ${firstInvalidRow.traNo} before saving.`);
            return;
        }

        if (!selectedAccountCode) {
            toast.showError("Select an account before saving.");
            return;
        }

        if (!selectedPaymode) {
            toast.showError("Select a payment mode before saving.");
            return;
        }

        if (paymentDetailKind === "cheque" && !chequeNo.trim()) {
            toast.showError("Enter the cheque number before saving.");
            return;
        }

        if (paymentDetailKind === "card" && !cardReference.trim()) {
            toast.showError("Enter the card reference before saving.");
            return;
        }

        if (paymentDetailKind === "other" && !otherReference.trim()) {
            toast.showError("Enter the other payment reference before saving.");
            return;
        }

        const allocatedRows = rows
            .map((row) => ({
                transactionId: row.transactionId,
                traNo: row.traNo,
                traType: row.traType,
                accountNo: row.accountNo,
                traAmount: row.traAmount,
                allcamount: parseAmountValue(row.amount) ?? 0,
            }))
            .filter((row) => row.traNo && row.allcamount > 0);

        if (!allocatedRows.length) {
            toast.showError("Enter at least one allocated amount before saving.");
            return;
        }

        const selectedCustomer = customerHook.customers.find(
            (customer) => (customer.cus_code ?? "") === selectedAccountCode,
        );
        const activeBranch = user?.active_branch;
        const locationLabel = activeBranch?.loc_code
            ? `${activeBranch.loc_code}${activeBranch.loc_desc ? ` - ${activeBranch.loc_desc}` : ""}`
            : user?.active_branch_id || "-";

        try {
            setSaving(true);
            const result = await saveAccountEntriesPayment({
                membercode: selectedAccountCode,
                paymode: selectedPaymode,
                allocated: allocatedRows,
                party_type: partyType,
                payment_desc: paymentDetailKind === "other" ? otherReference.trim() : null,
                cheque_no: paymentDetailKind === "cheque" ? chequeNo.trim() : null,
                card_reference: paymentDetailKind === "card" ? cardReference.trim() : null,
                other_reference: paymentDetailKind === "other" ? otherReference.trim() : null,
                payer_name: payerName.trim() || null,
                reference_no: referenceNo.trim() || null,
                note: note.trim() || null,
                cheque_date: paymentDetailKind === "cheque" && chequeDate ? chequeDate : null,
            });

            setSearchText("");
            setRows([]);
            setRowErrors({});
            setRowsError(null);
            setCustomerDrawerOpen(false);
            setDrawerSearchTerm("");
            setChequeNo("");
            setChequeDate("");
            setCardReference("");
            setOtherReference("");
            setPayerName("");
            setReferenceNo("");
            setNote("");

            if (accountsMode === "customer-receipts") {
                setSavedReceipt({
                    transactionNo: Array.from(new Set(result.docnos)).join(", ") || "-",
                    customerCode: selectedCustomer?.cus_code ?? selectedAccountCode,
                    customerName: selectedCustomer?.cus_name ?? "",
                    userName: user?.user_name ?? "-",
                    locationLabel,
                    paymentModeLabel: selectedPaymentModeLabel,
                    chequeNo: paymentDetailKind === "cheque" ? chequeNo.trim() : "",
                    chequeDate: paymentDetailKind === "cheque" ? chequeDate : "",
                    payerName: payerName.trim(),
                    referenceNo: referenceNo.trim(),
                    note: note.trim(),
                    amount: allocatedRows.reduce((total, row) => total + row.allcamount, 0),
                    transactionDate: new Date().toLocaleString(),
                    companyName: companySetup?.setup_comname || "",
                    companyAddress: companySetup?.setup_comaddress || "",
                });
                setHistoryRefreshKey((key) => key + 1);
            }

            const docSummary = result.docnos.length ? ` (${result.docnos.join(", ")})` : "";
            toast.showSuccess(`Account entries saved successfully${docSummary}.`);
        } catch (error: any) {
            toast.showError(error?.message || "Failed to save account entries.");
        } finally {
            setSaving(false);
        }
    };

    const handlePrintReceipt = (receipt: SavedReceiptSnapshot | null = savedReceipt) => {
        if (!receipt) {
            return;
        }

        if (!printCustomerReceipt(receipt)) {
            toast.showError("Unable to open print window. Please allow pop-ups and try again.");
        }
    };

    const handleReprintReceipt = (receipt: CustomerReceipt) => {
        handlePrintReceipt({
            transactionNo: receipt.docnos.join(", ") || "-",
            customerCode: receipt.customer_code,
            customerName: receipt.customer_name,
            userName: receipt.user_name || "-",
            locationLabel: receipt.location_label || "-",
            paymentModeLabel: receipt.payment_mode,
            chequeNo: receipt.cheque_no,
            chequeDate: receipt.cheque_date,
            payerName: receipt.payer_name,
            referenceNo: receipt.reference_no,
            note: receipt.note,
            amount: receipt.amount,
            transactionDate: formatReceiptDate(receipt.created_at),
            companyName: companySetup?.setup_comname || "",
            companyAddress: companySetup?.setup_comaddress || "",
        });
    };

    const handleSelectAccount = (code: string) => {
        setSearchText(code);
        setCustomerDrawerOpen(false);
        setDrawerSearchTerm("");
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    if (loading) {
        return <Loading message="Loading accounts..." />;
    }

    return (
        <Box padding="medium" className="accounts-page">
            <Stack space="large">

                {error ? <Alert tone="critical"><Text>{error}</Text></Alert> : null}
                {rowsError ? <Alert tone="critical"><Text>{rowsError}</Text></Alert> : null}

                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong">{panelTitle}</Text>
                            <div className="accounts-search-row">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={searchText}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setSearchText(value);
                                    }}
                                    placeholder={fieldPlaceholder}
                                    title={searchLabel}
                                    aria-label={searchLabel}
                                    list={fieldListId}
                                    autoComplete="off"
                                    className="accounts-control"
                                />
                                <div className="accounts-search-button-wrap">
                                    <Button size="small" variant="ghost" onClick={() => setCustomerDrawerOpen(true)}>
                                        Search
                                    </Button>
                                </div>
                            </div>
                            <datalist id={fieldListId}>
                                {accountOptions.map((option) => (
                                    <option key={option.value} value={option.value} label={option.label}>
                                        {option.label}
                                    </option>
                                ))}
                            </datalist>
                            <div className="accounts-search-hint">
                                {accountsMode === "customer-receipts"
                                    ? "Type a customer code or name."
                                    : "Type a vendor code or name."}
                            </div>
                        </Stack>
                    </Box>
                </Card>

                <Drawer
                    title={selectedHook.drawerTitle}
                    open={isCustomerDrawerOpen}
                    onClose={() => setCustomerDrawerOpen(false)}
                    width="large"
                >
                    <Box padding="small">
                        <Stack space="small">
                            <TextField
                                label={accountsMode === "customer-receipts" ? "Search customers" : "Search vendors"}
                                value={drawerSearchTerm}
                                onChange={(e) => setDrawerSearchTerm(e.currentTarget.value)}
                                placeholder={accountsMode === "customer-receipts" ? "Search by number, code or name" : "Search vendor by code or name"}
                            />

                            <Box>
                                <Table label={accountsMode === "customer-receipts" ? "Customer search results" : "Vendor search results"}>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell>Code</TableHeaderCell>
                                            <TableHeaderCell>Name</TableHeaderCell>
                                            <TableHeaderCell align="right">Action</TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(accountsMode === "customer-receipts" ? customerHook.customers : vendorHook.vendors)
                                            .filter(item => Boolean((accountsMode === "customer-receipts" ? (item as any).cus_active : (item as any).ven_active)))
                                            .filter(item => {
                                                const q = drawerSearchTerm.trim().toLowerCase();
                                                if (!q) return true;
                                                if (accountsMode === "customer-receipts") {
                                                    const c = item as any;
                                                    return `${c.cus_code ?? ""} ${c.cus_name ?? ""}`.toLowerCase().includes(q);
                                                }
                                                const v = item as any;
                                                return `${v.ven_code ?? ""} ${v.ven_Name ?? ""}`.toLowerCase().includes(q);
                                            })
                                            .slice(0, 200)
                                            .map((item) => (
                                                <TableRow key={(accountsMode === "customer-receipts" ? (item as any).cus_code : (item as any).ven_code) || Math.random()}>
                                                    <TableCell>{accountsMode === "customer-receipts" ? (item as any).cus_code : (item as any).ven_code}</TableCell>
                                                    <TableCell>{accountsMode === "customer-receipts" ? (item as any).cus_name : (item as any).ven_Name}</TableCell>
                                                    <TableCell align="right">
                                                        <Inline space="xsmall" alignY="center" reverse={false} collapseBelow="tablet">
                                                            <Button
                                                                size="small"
                                                                variant="ghost"
                                                                onClick={() => {
                                                                    const code = accountsMode === "customer-receipts" ? (item as any).cus_code ?? "" : (item as any).ven_code ?? "";
                                                                    handleSelectAccount(code);
                                                                }}
                                                            >
                                                                Select
                                                            </Button>
                                                        </Inline>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Stack>
                    </Box>
                </Drawer>

                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong">Account Entries</Text>
                            {rowsLoading ? <Text>Loading account entries...</Text> : null}
                            <div className="accounts-table-wrap">
                                <table className="accounts-table">
                                    <thead>
                                        <tr>
                                            {[
                                                "Transaction No",
                                                "Transaction Type",
                                                "Date",
                                                "Account No",
                                                "Transaction Amount",
                                                "Allocated Amount",
                                            ].map((column) => (
                                                <th key={column} className="accounts-table-head-cell">
                                                    {column}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length > 0 ? (
                                            rows.map((row) => (
                                                <tr key={row.id}>
                                                    <td className="accounts-table-cell">
                                                        <span className="accounts-table-value">{row.traNo}</span>
                                                    </td>
                                                    <td className="accounts-table-cell">
                                                        <span className="accounts-table-value">{row.traType}</span>
                                                    </td>
                                                    <td className="accounts-table-cell">
                                                        <span className="accounts-table-value">{row.date}</span>
                                                    </td>
                                                    <td className="accounts-table-cell">
                                                        <span className="accounts-table-value">{row.accountNo}</span>
                                                    </td>
                                                    <td className="accounts-table-cell">
                                                        <span className="accounts-table-value">{row.traAmount}</span>
                                                    </td>
                                                    <td className="accounts-table-cell">
                                                        <div className="accounts-amount-field">
                                                            <input
                                                                value={row.amount}
                                                                onChange={(event) => handleAmountChange(row.id, event.target.value)}
                                                                onFocus={() => handleAmountFocus(row.id)}
                                                                placeholder="Allocated Amount"
                                                                title="Allocated Amount"
                                                                inputMode="decimal"
                                                                className={`accounts-control accounts-amount-input${rowErrors[row.id] ? " accounts-control-error" : ""}`}
                                                            />
                                                            {rowErrors[row.id] ? (
                                                                <span className="accounts-row-error">{rowErrors[row.id]}</span>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="accounts-table-cell" colSpan={6}>
                                                    <span className="accounts-table-empty">
                                                        {selectedAccountCode
                                                            ? "No matching account entries were found."
                                                            : "Select a supplier or customer to load account entries."}
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Stack>
                    </Box>
                </Card>

                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong">Payment</Text>
                            {paymentModesLoading ? <Text>Loading payment modes...</Text> : null}
                            {paymentModesError ? (
                                <Alert tone="critical">
                                    <Text>{paymentModesError}</Text>
                                </Alert>
                            ) : null}
                            <div className="accounts-payment-layout">
                                <div className="accounts-payment-summary" aria-label="Payment summary">
                                    <div className="accounts-payment-summary-field">
                                        <span className="accounts-payment-summary-label">Allocated Total</span>
                                        <span className="accounts-payment-summary-value accounts-payment-summary-amount">
                                            {formatAmountValue(allocatedAmountTotal)}
                                        </span>
                                    </div>
                                    <div className="accounts-payment-summary-field">
                                        <span className="accounts-payment-summary-label">Payment Mode</span>
                                        <span className="accounts-payment-summary-value">
                                            {selectedPaymentModeLabel || "-"}
                                        </span>
                                    </div>
                                </div>
                                <div className="accounts-payment-tiles" role="list" aria-label="Payment modes">
                                    {paymentModes.map((mode) => {
                                        const label = mode.paytypedesc?.trim() || mode.paycode;
                                        const isSelected = mode.paycode === selectedPaymode;

                                        return (
                                            <button
                                                key={mode.paycode}
                                                type="button"
                                                className={`accounts-payment-tile${isSelected ? " is-selected" : ""}`}
                                                onClick={() => setSelectedPaymode(mode.paycode)}
                                                role="listitem"
                                                data-selected={isSelected ? "true" : "false"}
                                            >
                                                <span className="accounts-payment-tile-label">{label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {paymentDetailKind !== "none" ? (
                                    <div className="accounts-payment-details">
                                        {paymentDetailKind === "cheque" ? (
                                            <>
                                                <label className="accounts-payment-detail-field">
                                                    <span className="accounts-payment-summary-label">Cheque Number</span>
                                                    <input
                                                        value={chequeNo}
                                                        onChange={(event) => setChequeNo(event.target.value)}
                                                        className="accounts-control"
                                                        placeholder="Cheque number"
                                                    />
                                                </label>
                                                <label className="accounts-payment-detail-field">
                                                    <span className="accounts-payment-summary-label">Cheque Date</span>
                                                    <input
                                                        type="date"
                                                        value={chequeDate}
                                                        onChange={(event) => setChequeDate(event.target.value)}
                                                        className="accounts-control"
                                                    />
                                                </label>
                                            </>
                                        ) : null}
                                        {paymentDetailKind === "card" ? (
                                            <label className="accounts-payment-detail-field">
                                                <span className="accounts-payment-summary-label">Card Reference</span>
                                                <input
                                                    value={cardReference}
                                                    onChange={(event) => setCardReference(event.target.value)}
                                                    className="accounts-control"
                                                    placeholder="Card approval or reference"
                                                    maxLength={25}
                                                />
                                            </label>
                                        ) : null}
                                        {paymentDetailKind === "other" ? (
                                            <label className="accounts-payment-detail-field accounts-payment-detail-field-wide">
                                                <span className="accounts-payment-summary-label">Other Reference</span>
                                                <input
                                                    value={otherReference}
                                                    onChange={(event) => setOtherReference(event.target.value)}
                                                    className="accounts-control"
                                                    placeholder="Reference or note"
                                                    maxLength={25}
                                                />
                                            </label>
                                        ) : null}
                                    </div>
                                ) : null}
                                {accountsMode === "customer-receipts" ? (
                                    <div className="accounts-payment-details">
                                        <label className="accounts-payment-detail-field">
                                            <span className="accounts-payment-summary-label">Payer Name (optional)</span>
                                            <input
                                                value={payerName}
                                                onChange={(event) => setPayerName(event.target.value)}
                                                className="accounts-control"
                                                placeholder="Name of the person paying"
                                                maxLength={50}
                                            />
                                        </label>
                                        <label className="accounts-payment-detail-field">
                                            <span className="accounts-payment-summary-label">Reference No (optional)</span>
                                            <input
                                                value={referenceNo}
                                                onChange={(event) => setReferenceNo(event.target.value)}
                                                className="accounts-control"
                                                placeholder="Reference number"
                                                maxLength={50}
                                            />
                                        </label>
                                        <label className="accounts-payment-detail-field accounts-payment-detail-field-wide">
                                            <span className="accounts-payment-summary-label">Note (optional)</span>
                                            <input
                                                value={note}
                                                onChange={(event) => setNote(event.target.value)}
                                                className="accounts-control"
                                                placeholder="Note to print on the receipt"
                                                maxLength={200}
                                            />
                                        </label>
                                    </div>
                                ) : null}
                            </div>
                        </Stack>
                    </Box>
                </Card>

                {accountsMode === "customer-receipts" ? (
                    <CustomerReceiptHistoryViewer refreshKey={historyRefreshKey} onReprint={handleReprintReceipt} />
                ) : null}

                <Box display="flex" justifyContent="flexEnd" gap="small">
                    {accountsMode === "customer-receipts" ? (
                        <div className={`accounts-print-button${savedReceipt ? "" : " is-disabled"}`}>
                            <Button
                                size="small"
                                tone="neutral"
                                variant="soft"
                                onClick={savedReceipt ? () => handlePrintReceipt() : undefined}
                            >
                                Print
                            </Button>
                        </div>
                    ) : null}
                    <div className="accounts-save-button">
                        <Button size="small" tone="brandAccent" variant="solid" onClick={handleSave} loading={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </Box>
            </Stack>
        </Box>
    );
};

export default Accounts;
